import {
  type Estudo,
  type Pessoa,
  type Sessao,
  type Visita,
  duracaoNoPeriodoMs,
  type Limites,
  limitarCreditos,
  limitesDoDia,
  limitesDoMes,
  limitesEmMilissegundos,
  minutosDaSoma,
  normalizarTexto,
} from '@sheafyield/dominio';
import type { Banco } from '@sheafyield/sync';

// As leituras que as telas fazem do banco local, num só lugar e sem React,
// para poderem ser testadas com `fake-indexeddb` sem montar componente.
//
// Duas regras valem para todas:
//
// 1. Linha com `deleted: true` é lápide, não dado. Nenhuma consulta a
//    devolve — é isso que impede um registro apagado de reaparecer na tela
//    até o próximo ciclo de sincronização levá-lo embora de vez.
// 2. Os limites de dia e de mês vêm do domínio (`limitesDoDia`,
//    `limitesDoMes`), que os calcula no fuso do usuário e os devolve em ISO
//    com `Z` (ADR-0003). Como o formato é único e de largura fixa, comparar
//    as strings no índice do Dexie é comparar os instantes.

function vivas<T extends { deleted: boolean }>(linhas: T[]): T[] {
  return linhas.filter((l) => !l.deleted);
}

/** Sessões que **começaram** dentro do dia de `agora`, da mais antiga à mais
 *  nova. Esta é uma lista de inícios para a cronologia da tela, não uma
 *  agregação: uma sessão vinda da véspera pode contribuir para o cartão de
 *  minutos por sobreposição sem aparecer nesta lista. */
export async function sessoesDoDia(
  banco: Banco,
  agora: Date = new Date(),
): Promise<Sessao[]> {
  return sessoesEntre(banco, limitesDoDia(agora));
}

async function sessoesEntre(banco: Banco, limites: Limites): Promise<Sessao[]> {
  return vivas(
    await banco.sessao
      .where('inicio')
      .between(limites.de, limites.ate, true, true)
      .toArray(),
  );
}

/** Candidatas a contribuir para um período: toda sessão iniciada antes do
 *  fim exclusivo. O recorte exato por sobreposição acontece depois, no
 *  domínio; buscar apenas a partir do início perderia a sessão que atravessa
 *  a meia-noite. */
async function sessoesCandidatasAoPeriodo(
  banco: Banco,
  limites: Limites,
): Promise<Sessao[]> {
  const fimExclusivo = new Date(
    limitesEmMilissegundos(limites).fim,
  ).toISOString();
  return vivas(
    await banco.sessao.where('inicio').below(fimExclusivo).toArray(),
  );
}

/** A sessão em curso: a de `fim` nulo mais recente, se houver. Pausar fecha
 *  a sessão e retomar abre outra, então no máximo uma fica aberta — mas se
 *  duas escaparem (dois aparelhos, mesma conta), vale a que começou depois.
 *  `fim` nulo não entra em índice do IndexedDB, daí o `filter`.
 *  O `filter` varre a tabela inteira, e é de propósito: o volume de um
 *  publicador (algumas centenas de sessões por ano) torna a varredura
 *  irrelevante perto da complexidade de manter um índice para isso. */
export async function sessaoAberta(banco: Banco): Promise<Sessao | undefined> {
  return banco.sessao
    .orderBy('inicio')
    .filter((s) => !s.deleted && s.fim === null)
    .last();
}

/** Os dois baldes que o relatório entrega: tempo de campo e créditos. */
export interface MinutosPorBalde {
  campo: number;
  creditos: number;
}

/** Minutos somados, separados em campo e créditos — **a mesma conta do
 *  relatório** (`minutosDoMes`), e não a soma de tudo junto.
 *
 *  Por que separados: a tela Hoje somava as cinco categorias num número só,
 *  e o `briefing` apresenta esses números como "os dois que o usuário
 *  confere antes de fechar o relatório". Quem cronometra em "Crédito" — LDC,
 *  Betel, escola, socorro, que é justamente por que a categoria existe —
 *  via na tela Hoje um total que o relatório nunca ia mostrar, sem nada
 *  dizendo isso.
 *
 *  A soma é em milissegundos e o truncamento é um só por balde, no fim, pelo
 *  mesmo motivo de `minutosDoMes`: pausar fecha a sessão e retomar abre
 *  outra, e truncar cada uma jogaria fora até 59 s por parada. Um
 *  truncamento por balde, e não um sobre o total, porque os baldes vão para
 *  linhas diferentes do relatório.
 *
 *  A sessão aberta conta até `agora` **ou até a meia-noite do dia em que
 *  começou** — o teto mora na duração do domínio, para o mostrador do
 *  cronômetro e estes cartões nunca discordarem. */
function minutosDe(
  sessoes: readonly Sessao[],
  limites: Limites,
  agora: Date,
): MinutosPorBalde {
  let campoMs = 0;
  let creditosMs = 0;
  for (const s of sessoes) {
    const ms = duracaoNoPeriodoMs(s, limites, agora);
    if (s.categoria === 'credito') creditosMs += ms;
    else campoMs += ms;
  }
  return {
    campo: minutosDaSoma(campoMs),
    creditos: minutosDaSoma(creditosMs),
  };
}

/** Campo e créditos do dia. Sem teto de créditos: o teto de 55 h é do
 *  **mês** (`limitarCreditos`), e aplicá-lo ao dia seria inventar uma regra
 *  que o relatório não tem. */
export async function minutosDeHoje(
  banco: Banco,
  agora: Date = new Date(),
): Promise<MinutosPorBalde> {
  const limites = limitesDoDia(agora);
  return minutosDe(
    await sessoesCandidatasAoPeriodo(banco, limites),
    limites,
    agora,
  );
}

/** O mesmo para o mês de calendário que contém `agora`, **com o teto de
 *  créditos do relatório** (55 h): é o número que vai ser entregue, e
 *  mostrar mais do que isso na tela Hoje seria prometer horas que o
 *  relatório não aceita. */
export async function minutosDoMesCorrente(
  banco: Banco,
  agora: Date = new Date(),
): Promise<MinutosPorBalde> {
  const limites = limitesDoMes(agora.getFullYear(), agora.getMonth() + 1);
  const { campo, creditos } = minutosDe(
    await sessoesCandidatasAoPeriodo(banco, limites),
    limites,
    agora,
  );
  return { campo, creditos: limitarCreditos(creditos) };
}

/** Quem visitar hoje: `proxima_visita_em` até o fim do dia, o que inclui as
 *  atrasadas — quem ficou de ontem continua aparecendo até ser visitado. Da
 *  mais atrasada para a mais recente, que é a ordem em que se deve ir.
 *  Pessoa sem `proxima_visita_em` não entra (nulo fica fora do índice).
 *
 *  **`nao_visitar` fica de fora**, mesmo com data agendada. A data é sempre
 *  anterior à marcação — ninguém marca "não visitar" e agenda em seguida —,
 *  então sem este filtro a marcação não teria efeito nenhum na única tela em
 *  que ela importa: quem pediu para não ser visitado voltaria à tira do dia
 *  toda manhã. O filtro é em memória porque o índice é o de
 *  `proxima_visita_em`; o recorte já vem pequeno (as pessoas de um dia). */
export async function paraVisitarHoje(
  banco: Banco,
  agora: Date = new Date(),
): Promise<Pessoa[]> {
  return vivas(
    await banco.pessoa
      .where('proxima_visita_em')
      .belowOrEqual(limitesDoDia(agora).ate)
      .toArray(),
  ).filter((p) => p.status !== 'nao_visitar');
}

/** Visitas do dia, da mais antiga à mais nova. */
export async function visitasDoDia(
  banco: Banco,
  agora: Date = new Date(),
): Promise<Visita[]> {
  const limites = limitesDoDia(agora);
  return vivas(
    await banco.visita
      .where('em')
      .between(limites.de, limites.ate, true, true)
      .toArray(),
  );
}

/** O nome a mostrar em cada visita que tem ficha.
 *
 * `null` no mapa é "a ficha existiu e foi apagada"; ausente do mapa é
 * "nunca teve ficha" — a visita do caminho de dois toques. Os dois casos
 * se separam sem coluna nova: `pessoa_id` nulo é anônimo, e `pessoa_id`
 * que resolve para uma linha com `deleted` é apagada. É por isso que esta
 * consulta lê a lápide em vez de filtrá-la, contra a regra geral do
 * arquivo — aqui a lápide **é** a informação. */
export async function nomesDasVisitas(
  banco: Banco,
  visitas: readonly Visita[],
): Promise<Map<string, string | null>> {
  const nomes = new Map<string, string | null>();
  for (const visita of visitas) {
    if (visita.pessoa_id === null) continue;
    const pessoa = await banco.pessoa.get(visita.pessoa_id);
    if (pessoa === undefined) {
      // Ficha que nunca chegou a este aparelho: tratar como apagada é o
      // mais próximo da verdade — houve alguém, e não sabemos quem.
      nomes.set(visita.id, null);
      continue;
    }
    nomes.set(
      visita.id,
      pessoa.deleted
        ? null
        : pessoa.nome.trim() === ''
          ? pessoa.endereco
          : pessoa.nome,
    );
  }
  return nomes;
}

/** A publicação da visita mais recente que registrou alguma — o padrão do
 *  campo "publicação" na folha Registrar, que evita um toque no caminho
 *  curto. `undefined` quando nunca se deixou publicação. */
export async function ultimaPublicacaoUsada(
  banco: Banco,
): Promise<string | undefined> {
  const visita = await banco.visita
    .orderBy('em')
    .reverse()
    .filter((v) => !v.deleted && v.publicacao !== '')
    .first();
  return visita?.publicacao;
}

/** As publicações já usadas, sem repetir, da usada mais recentemente para a
 *  mais antiga — a ordem em que a lista de sugestões deve aparecer.
 *  Percorre todas as visitas, e no volume de um publicador isso é
 *  irrelevante; se um dia a folha Registrar pesar ao abrir, a correção é
 *  parar nas N visitas mais recentes, não indexar. */
export async function publicacoesUsadas(banco: Banco): Promise<string[]> {
  const vistas = new Set<string>();
  await banco.visita
    .orderBy('em')
    .reverse()
    .filter((v) => !v.deleted && v.publicacao !== '')
    .each((v) => {
      vistas.add(v.publicacao);
    });
  return [...vistas];
}

/** Nome como chave de comparação: sem acento, sem caixa, sem espaço
 *  sobrando. "  José   da Silva " e "jose da silva" são a mesma pessoa —
 *  quem digita o nome no meio da rua, com uma mão, não repete a acentuação
 *  de ontem.
 *
 *  A normalização é a **do domínio**, a mesma que interpreta "sábado 10h":
 *  duas cópias divergiriam no dia em que uma delas aprendesse o hífen, e aí
 *  "José" e "Jose" seriam a mesma pessoa só num dos dois lugares. */
const chaveDoNome = normalizarTexto;

/** Todas as pessoas vivas com esse nome, da mexida mais recentemente para a
 *  mais antiga. Nome vazio nunca casa com ninguém.
 *
 *  Devolve **todas**, e não a mais recente, porque é a folha Registrar que
 *  decide: com uma candidata ela vincula direto e o caminho de dois toques
 *  fica intacto; com duas ou mais ela pergunta qual. Eleger aqui era o que
 *  fazia duas "Maria" diferentes virarem a mesma ficha, com a segunda visita
 *  sobrescrevendo o `status` e a `proxima_visita_em` da primeira (decisão de
 *  08/09/2026, briefing de Pessoas).
 *
 *  Varre a tabela porque não há índice por nome normalizado — e no volume de
 *  um publicador (algumas centenas de pessoas) isso é irrelevante. */
export async function pessoasPorNome(
  banco: Banco,
  nome: string,
): Promise<Pessoa[]> {
  const chave = chaveDoNome(nome);
  if (chave === '') return [];
  const achadas: Pessoa[] = [];
  await banco.pessoa.each((p) => {
    if (p.deleted || chaveDoNome(p.nome) !== chave) return;
    achadas.push(p);
  });
  return achadas.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

/** Os quatro recortes da lista de Pessoas (briefing, prancha 03.1). */
export type FiltroDePessoas =
  'todos' | 'revisitas' | 'estudos' | 'interessados';

const STATUS_DO_FILTRO: Record<
  Exclude<FiltroDePessoas, 'todos'>,
  Pessoa['status']
> = {
  revisitas: 'revisita',
  estudos: 'estudo',
  interessados: 'interessado',
};

/** As pessoas que a lista mostra: vivas, visitáveis, casando com o termo e
 *  com o filtro, da mexida mais recentemente para a mais antiga.
 *
 *  `nao_visitar` fica de fora: a lista é para escolher quem visitar, e quem
 *  pediu para não ser visitado não é candidato. A ficha continua alcançável
 *  por link direto — o pedido é para não bater na porta, não para apagar o
 *  registro.
 *
 *  A busca considera nome **e** endereço porque metade das fichas do campo
 *  não tem nome nenhum: o que se lembra é a casa. Normaliza os dois lados
 *  com a mesma função do domínio, a que já decide homônimos. */
export async function pessoasDaLista(
  banco: Banco,
  termo: string,
  filtro: FiltroDePessoas,
): Promise<Pessoa[]> {
  const chave = normalizarTexto(termo);
  const status = filtro === 'todos' ? null : STATUS_DO_FILTRO[filtro];
  const achadas = vivas(await banco.pessoa.toArray()).filter((p) => {
    if (p.status === 'nao_visitar') return false;
    if (status !== null && p.status !== status) return false;
    if (chave === '') return true;
    return (
      normalizarTexto(p.nome).includes(chave) ||
      normalizarTexto(p.endereco).includes(chave)
    );
  });
  return achadas.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

/** Os filtros que têm conteúdo — os outros não aparecem na tela (briefing).
 *  Um filtro que devolve lista vazia é um botão que só serve para
 *  decepcionar, e em AX5 ele custa uma linha inteira da primeira dobra. */
export async function filtrosComConteudo(
  banco: Banco,
): Promise<FiltroDePessoas[]> {
  const todas = vivas(await banco.pessoa.toArray()).filter(
    (p) => p.status !== 'nao_visitar',
  );
  if (todas.length === 0) return [];
  const oferecidos: FiltroDePessoas[] = ['todos'];
  for (const filtro of ['revisitas', 'estudos', 'interessados'] as const) {
    if (todas.some((p) => p.status === STATUS_DO_FILTRO[filtro]))
      oferecidos.push(filtro);
  }
  return oferecidos;
}

/** A ficha, ou `undefined` — inclusive quando a linha existe mas é lápide:
 *  para a tela, apagada e inexistente são a mesma coisa. */
export async function pessoaPorId(
  banco: Banco,
  id: string,
): Promise<Pessoa | undefined> {
  const achada = await banco.pessoa.get(id);
  return achada === undefined || achada.deleted ? undefined : achada;
}

/** O histórico da ficha, da visita mais recente para a mais antiga: é a
 *  ordem em que a timeline da prancha 03.2 lê. O primeiro campo do índice
 *  composto `[pessoa_id+em]` é um índice virtual do Dexie, portanto esta
 *  consulta não exige duplicar `pessoa_id` no esquema. */
export async function visitasDaPessoa(
  banco: Banco,
  id: string,
): Promise<Visita[]> {
  const todas = vivas(
    await banco.visita.where('pessoa_id').equals(id).toArray(),
  );
  return todas.sort((a, b) => b.em.localeCompare(a.em));
}

/** Os estudos da pessoa, do mais recente para o mais antigo. */
export async function estudosDaPessoa(
  banco: Banco,
  id: string,
): Promise<Estudo[]> {
  const todos = vivas(
    await banco.estudo.where('pessoa_id').equals(id).toArray(),
  );
  return todos.sort((a, b) => b.iniciado_em.localeCompare(a.iniciado_em));
}

/** Se apagar esta ficha deixa visitas ou estudos vivos para trás — o que
 *  decide entre a confirmação da prancha 14.2 e o apagar direto. */
export async function temHistorico(banco: Banco, id: string): Promise<boolean> {
  const visitas = await banco.visita.where('pessoa_id').equals(id).toArray();
  if (visitas.some((v) => !v.deleted)) return true;
  const estudos = await banco.estudo.where('pessoa_id').equals(id).toArray();
  return estudos.some((e) => !e.deleted);
}

/** A edição de telefone ou nota muda `updated_at`, mas não é uma visita.
 *  A lista junta a ficha ao último acontecimento vivo, usando `em`; ausência
 *  explícita deixa a tela dizer honestamente que ainda não há visitas. */
export async function pessoasComUltimaVisita(
  banco: Banco,
  termo: string,
  filtro: FiltroDePessoas,
): Promise<Array<{ pessoa: Pessoa; ultimaVisita: Visita | null }>> {
  return banco.transaction('r', banco.pessoa, banco.visita, async () => {
    const pessoas = await pessoasDaLista(banco, termo, filtro);
    return Promise.all(
      pessoas.map(async (pessoa) => ({
        pessoa,
        ultimaVisita: (await visitasDaPessoa(banco, pessoa.id))[0] ?? null,
      })),
    );
  });
}
